import React, { useState, useEffect, useCallback, useRef } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { useS3Audio } from '../hooks/useS3Audio';
import { flashCorrect, flashWrong, Confetti } from '../lib/animations';
import { VoyageurCanoe, GameHeader, QuestionCard } from '../lib/GameVisuals';
import { Trophy, Volume2 } from 'lucide-react';

function RiverRaceScene({ team1Progress, team2Progress, target, team1Name, team2Name }) {
  return (
    <div style={{
      flex: 1, position: 'relative', overflow: 'hidden',
      borderRadius: '1.25rem', margin: '0 0.75rem',
      border: '2px solid rgba(0,0,0,0.08)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 800 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="sky-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#87CEEB" />
            <stop offset="100%" stopColor="#B8E4F9" />
          </linearGradient>
          <linearGradient id="water-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2196F3" />
            <stop offset="100%" stopColor="#0D47A1" />
          </linearGradient>
        </defs>
        <rect width="800" height="90" fill="url(#sky-g)" />
        <circle cx="720" cy="30" r="22" fill="#FFD43B" opacity="0.9" />
        <ellipse cx="100" cy="25" rx="35" ry="12" fill="white" opacity="0.7" />
        <ellipse cx="400" cy="18" rx="28" ry="10" fill="white" opacity="0.5" />
        <polygon points="0,90 50,50 100,90" fill="#5B8A4E" />
        <polygon points="70,90 140,35 210,90" fill="#4A7A3E" />
        <polygon points="180,90 260,40 340,90" fill="#5B8A4E" />
        <polygon points="300,90 380,45 460,90" fill="#4A7A3E" />
        <polygon points="420,90 510,38 600,90" fill="#5B8A4E" />
        <polygon points="560,90 650,42 740,90" fill="#4A7A3E" />
        <polygon points="690,90 760,50 800,90" fill="#5B8A4E" />
        <polygon points="140,35 130,50 150,50" fill="white" opacity="0.6" />
        <polygon points="260,40 248,55 272,55" fill="white" opacity="0.5" />
        <polygon points="510,38 498,52 522,52" fill="white" opacity="0.6" />
        <rect x="0" y="88" width="800" height="14" fill="#3A7A2F" />
        {[30, 80, 140, 210, 280, 350, 430, 500, 570, 640, 710, 770].map((tx, i) => (
          <polygon key={i} points={`${tx},88 ${tx - 5},78 ${tx + 5},78`} fill="#2D6A4F" />
        ))}
        <rect x="0" y="102" width="800" height="298" fill="url(#water-g)" />
        <rect x="0" y="102" width="800" height="140" fill="rgba(255,255,255,0.04)" />
        <line x1="0" y1="242" x2="800" y2="242" stroke="rgba(255,255,255,0.25)" strokeWidth="2" strokeDasharray="16,10" />
        {[120, 160, 200, 270, 310, 350, 380].map((y, i) => (
          <path key={i}
            d={`M0,${y} Q100,${y - 4} 200,${y} Q300,${y + 4} 400,${y} Q500,${y - 4} 600,${y} Q700,${y + 4} 800,${y}`}
            fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5">
            <animate attributeName="d"
              values={`M0,${y} Q100,${y - 4} 200,${y} Q300,${y + 4} 400,${y} Q500,${y - 4} 600,${y} Q700,${y + 4} 800,${y};M0,${y + 3} Q100,${y + 3} 200,${y - 3} Q300,${y} 400,${y + 3} Q500,${y + 3} 600,${y - 3} Q700,${y} 800,${y + 3};M0,${y} Q100,${y - 4} 200,${y} Q300,${y + 4} 400,${y} Q500,${y - 4} 600,${y} Q700,${y + 4} 800,${y}`}
              dur={`${3 + (i % 3)}s`} repeatCount="indefinite" />
          </path>
        ))}
        <g transform="translate(750, 102)">
          <rect x="-2" y="0" width="12" height="298" fill="rgba(255,255,255,0.15)" />
          {Array.from({ length: 20 }).map((_, i) => (
            <rect key={i} x={i % 2 === 0 ? -2 : 4} y={i * 15} width="6" height="15" fill={i % 2 === 0 ? '#1A1A2E' : 'white'} opacity="0.8" />
          ))}
        </g>
        {/* Progress markers along lanes */}
        {target > 0 && Array.from({ length: Math.min(target - 1, 10) }).map((_, i) => {
          const markerX = 50 + ((i + 1) / target) * 680;
          return (
            <g key={`marker-${i}`}>
              <circle cx={markerX} cy="170" r="3" fill="rgba(255,255,255,0.2)" />
              <circle cx={markerX} cy="310" r="3" fill="rgba(255,255,255,0.2)" />
            </g>
          );
        })}
        <text x="756" y="96" textAnchor="middle" fontSize="16" fill="white" fontWeight="bold" fontFamily="Fredoka" opacity="1">FINISH</text>
        {/* Finish flag */}
        <rect x="748" y="80" width="3" height="22" fill="white" opacity="0.9" />
        <rect x="751" y="80" width="10" height="7" fill="#E03131" />
        <rect x="751" y="87" width="10" height="7" fill="white" />
      </svg>

      {/* Team 1 canoe */}
      <div style={{
        position: 'absolute', top: '38%', transform: 'translateY(-50%)',
        left: `${3 + team1Progress * 0.87}%`,
        transition: 'left 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
        zIndex: 5, filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))',
      }}>
        <VoyageurCanoe color="var(--team1)" size={110} animate={true} />
      </div>

      {/* Team 2 canoe */}
      <div style={{
        position: 'absolute', top: '72%', transform: 'translateY(-50%)',
        left: `${3 + team2Progress * 0.87}%`,
        transition: 'left 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
        zIndex: 5, filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))',
      }}>
        <VoyageurCanoe color="var(--team2)" size={110} animate={true} />
      </div>

      {/* Team labels */}
      <div style={{
        position: 'absolute', top: '28%', left: 10,
        background: 'var(--team1)', color: 'white',
        padding: '0.2rem 0.75rem', borderRadius: '2rem',
        fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)', zIndex: 6,
      }}>{team1Name}</div>
      <div style={{
        position: 'absolute', top: '62%', left: 10,
        background: 'var(--team2)', color: 'white',
        padding: '0.2rem 0.75rem', borderRadius: '2rem',
        fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700,
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)', zIndex: 6,
      }}>{team2Name}</div>
    </div>
  );
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function LaCourse({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('la_course'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const target = settings.target || 10;

  // Filter out items without options
  const validItems = items.filter(i => i.options && i.options.length > 0);

  // Team items: even-indexed for T1, odd-indexed for T2 (independent question pools)
  const t1Items = validItems.filter((_, i) => i % 2 === 0);
  const t2Items = validItems.filter((_, i) => i % 2 === 1);

  // Effective target: capped to what's achievable by the smaller team
  const effectiveTarget = Math.max(1, Math.min(target, t1Items.length, t2Items.length));

  const [t1Score, setT1Score] = useState(0);
  const [t2Score, setT2Score] = useState(0);
  const [t1Idx, setT1Idx] = useState(0);
  const [t2Idx, setT2Idx] = useState(0);
  const [t1Fb, setT1Fb] = useState(null);
  const [t2Fb, setT2Fb] = useState(null);
  const [gameTime, setGameTime] = useState(settings.gameTimer || 0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [showConfetti, setShowConfetti] = useState(0);
  const streakRef = useRef(0);
  const timerRef = useRef(null);
  const s1Ref = useRef(null);
  const s2Ref = useRef(null);
  const endedRef = useRef(false);
  // Refs for final scores (avoid stale closure in timer callback)
  const scoresRef = useRef({ t1: 0, t2: 0, answered: 0, correct: 0, streak: 0 });
  // Question review tracking
  const reviewRef = useRef([]);

  const t1Item = t1Items[t1Idx] || null;
  const t2Item = t2Items[t2Idx] || null;

  // Game-level countdown timer (independent of question answering)
  useEffect(() => {
    if (settings.gameTimer <= 0) return;
    timerRef.current = setInterval(() => {
      setGameTime(t => {
        if (t <= 1) {
          finishGameFromTimer();
          return 0;
        }
        if (t <= 11) playSound('tick');
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Onboarding tour can force-finish the round (visitor taps "Skip to results").
  // Reuse the timer-finish path because it pulls from refs and is safe to call
  // mid-game without waiting for state updates.

  // Timer finish uses refs (always current values)
  const finishGameFromTimer = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    clearInterval(timerRef.current);
    setShowConfetti(c => c + 1);
    playSound('win');
    const s = scoresRef.current;
    // Add unanswered questions for both teams
    const review = [...reviewRef.current];
    const t1Answered = review.filter(q => q.team === 1).length;
    const t2Answered = review.filter(q => q.team === 2).length;
    for (let i = t1Answered; i < t1Items.length; i++) {
      const q = t1Items[i]; if (!q) break;
      const correctOpt = (q.options || []).find(o => o.correct);
      review.push({ team: 1, prompt: q.prompt_text_fr || q.prompt_text_en || '?', correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null });
    }
    for (let i = t2Answered; i < t2Items.length; i++) {
      const q = t2Items[i]; if (!q) break;
      const correctOpt = (q.options || []).find(o => o.correct);
      review.push({ team: 2, prompt: q.prompt_text_fr || q.prompt_text_en || '?', correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null });
    }
    setTimeout(() => onGameEnd({
      team1Score: s.t1, team2Score: s.t2,
      totalQuestions: s.answered,
      accuracy: s.answered > 0 ? (s.correct / s.answered) * 100 : 0,
      bestStreak: s.streak,
      questionReview: review, reviewType: 'team_qa',
    }), 1500);
  }, [onGameEnd, playSound]);

  // Onboarding tour can force-finish the round (visitor taps "Skip to results").
  // Reuse the timer-finish path because it pulls from refs and is safe to call
  // mid-game without waiting for state updates.
  useEffect(() => {
    const onForceEnd = () => finishGameFromTimer();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [finishGameFromTimer]);

  // Target/exhaust finish uses state (current via effect deps)
  const finishGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    clearInterval(timerRef.current);
    setShowConfetti(c => c + 1);
    playSound('win');
    // Add unanswered questions for both teams
    const review = [...reviewRef.current];
    const t1Answered = review.filter(q => q.team === 1).length;
    const t2Answered = review.filter(q => q.team === 2).length;
    for (let i = t1Answered; i < t1Items.length; i++) {
      const q = t1Items[i]; if (!q) break;
      const correctOpt = (q.options || []).find(o => o.correct);
      review.push({ team: 1, prompt: q.prompt_text_fr || q.prompt_text_en || '?', correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null });
    }
    for (let i = t2Answered; i < t2Items.length; i++) {
      const q = t2Items[i]; if (!q) break;
      const correctOpt = (q.options || []).find(o => o.correct);
      review.push({ team: 2, prompt: q.prompt_text_fr || q.prompt_text_en || '?', correctAnswer: correctOpt?.text || '—', userAnswer: null, wasCorrect: null });
    }
    setTimeout(() => onGameEnd({
      team1Score: t1Score, team2Score: t2Score,
      totalQuestions: totalAnswered,
      accuracy: totalAnswered > 0 ? (totalCorrect / totalAnswered) * 100 : 0,
      bestStreak,
      questionReview: review, reviewType: 'team_qa',
    }), 1500);
  }, [t1Score, t2Score, totalAnswered, totalCorrect, bestStreak, onGameEnd, playSound]);

  // Check win conditions: effectiveTarget reached OR all items exhausted
  useEffect(() => {
    if (endedRef.current) return;
    if (t1Score >= effectiveTarget || t2Score >= effectiveTarget) {
      finishGame();
    } else if (!t1Item && !t2Item) {
      // Both teams out of questions
      finishGame();
    }
  }, [t1Score, t2Score, t1Item, t2Item, effectiveTarget, finishGame]);

  const handleAnswer = useCallback((team, optIdx, btnEl) => {
    const item = team === 1 ? t1Item : t2Item;
    if (!item) return;
    if ((team === 1 && t1Fb) || (team === 2 && t2Fb)) return;
    const correct = item.options[optIdx]?.correct;
    setTotalAnswered(a => {
      const v = a + 1;
      scoresRef.current.answered = v;
      // Let the onboarding tour gate "Skip to results" until the visitor has
      // actually answered a couple of questions; otherwise the EndScreen
      // shows zeros and the demo lands flat.
      try { window.dispatchEvent(new CustomEvent('frenchboard:answer-recorded', { detail: { count: v } })); } catch { /* noop */ }
      return v;
    });

    if (correct) {
      playSound('correct');
      setTotalCorrect(c => { const v = c + 1; scoresRef.current.correct = v; return v; });
      streakRef.current += 1;
      setBestStreak(b => { const v = Math.max(b, streakRef.current); scoresRef.current.streak = v; return v; });
      if (btnEl) flashCorrect(btnEl);
      const sRef = team === 1 ? s1Ref : s2Ref;
      if (sRef.current) gsap.fromTo(sRef.current, { scale: 1.5 }, { scale: 1, duration: 0.5, ease: 'elastic.out(1,0.5)' });
      // Track correct answer for review
      const correctOpt = (item.options || []).find(o => o.correct);
      reviewRef.current.push({
        team, prompt: item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—', userAnswer: item.options[optIdx]?.text || '—', wasCorrect: true,
      });
      if (team === 1) {
        setT1Score(s => { const v = s + 1; scoresRef.current.t1 = v; return v; });
        setT1Fb('correct');
        setTimeout(() => { setT1Fb(null); setT1Idx(i => i + 1); }, 500);
      } else {
        setT2Score(s => { const v = s + 1; scoresRef.current.t2 = v; return v; });
        setT2Fb('correct');
        setTimeout(() => { setT2Fb(null); setT2Idx(i => i + 1); }, 500);
      }
    } else {
      playSound('wrong');
      streakRef.current = 0;
      if (btnEl) flashWrong(btnEl);
      // Track wrong answer for review
      const correctOpt = (item.options || []).find(o => o.correct);
      reviewRef.current.push({
        team, prompt: item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || '—', userAnswer: item.options[optIdx]?.text || '—', wasCorrect: false,
      });
      if (team === 1) { setT1Fb('wrong'); setTimeout(() => setT1Fb(null), 500); }
      else { setT2Fb('wrong'); setTimeout(() => setT2Fb(null), 500); }
    }
  }, [t1Item, t2Item, t1Fb, t2Fb, playSound]);

  const t1Pct = effectiveTarget > 0 ? (t1Score / effectiveTarget) * 100 : 0;
  const t2Pct = effectiveTarget > 0 ? (t2Score / effectiveTarget) * 100 : 0;

  return (
    <div data-testid="la-course-game" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', overflow: 'hidden' }}>
      <Confetti trigger={showConfetti} />

      <GameHeader title="LA COURSE" icon={<Trophy size={24} color="var(--accent)" />} color="var(--accent)">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ background: 'var(--team1)', color: 'white', padding: '0.15rem 0.75rem', borderRadius: '2rem', fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700 }}>{settings.team1Name}</span>
            <span ref={s1Ref} className="font-score" style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--team1)' }} data-testid="team1-score">{t1Score}</span>
          </div>
          {settings.gameTimer > 0 && (
            <div className={`font-score ${gameTime <= 10 ? 'timer-critical' : ''}`}
              style={{ fontSize: '2rem', fontWeight: 800, color: gameTime <= 10 ? 'var(--error)' : 'var(--accent)' }}
              data-testid="game-timer">{formatTime(gameTime)}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span ref={s2Ref} className="font-score" style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--team2)' }} data-testid="team2-score">{t2Score}</span>
            <span style={{ background: 'var(--team2)', color: 'white', padding: '0.15rem 0.75rem', borderRadius: '2rem', fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700 }}>{settings.team2Name}</span>
          </div>
        </div>
      </GameHeader>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', padding: '0.5rem' }}>
        <TeamQuestionPanel team={1} item={t1Item} fb={t1Fb} onAnswer={handleAnswer} color="var(--team1)" bgColor="var(--team1-bg)" />
        <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column' }}>
          <RiverRaceScene team1Progress={t1Pct} team2Progress={t2Pct} target={effectiveTarget} team1Name={settings.team1Name} team2Name={settings.team2Name} />
          <div style={{ textAlign: 'center', padding: '0.4rem', fontFamily: 'Fredoka', fontSize: '1rem', color: 'var(--text-muted)' }}>
            First to {effectiveTarget}
          </div>
        </div>
        <TeamQuestionPanel team={2} item={t2Item} fb={t2Fb} onAnswer={handleAnswer} color="var(--team2)" bgColor="var(--team2-bg)" />
      </div>
    </div>
  );
}

function TeamQuestionPanel({ team, item, fb, onAnswer, color, bgColor }) {
  const panelRef = useRef(null);
  const { play: playAudio, stop: stopAudio, isPlaying } = useS3Audio();
  const hasImageOptions = (item?.options || []).some(o => o.image);

  useEffect(() => {
    if (panelRef.current && item) {
      gsap.fromTo(panelRef.current, { opacity: 0.5, y: 8 }, { opacity: 1, y: 0, duration: 0.25, ease: 'power2.out' });
    }
    return () => stopAudio();
  }, [item?.item_id]);

  if (!item) {
    return (
      <div style={{
        flex: 1, background: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '1rem', margin: '0.25rem',
      }}>
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.4rem', color: 'var(--text-muted)', textAlign: 'center', padding: '1.5rem' }}>
          All done!<br />
          <span style={{ fontSize: '1rem', opacity: 0.7 }}>Waiting for other team...</span>
        </div>
      </div>
    );
  }

  const imgBase = process.env.REACT_APP_IMAGE_BASE_URL || '';

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: '0.75rem', background: bgColor, gap: '0.5rem',
      borderRadius: '1rem', margin: '0.25rem',
      overflow: 'auto',
    }}>
      <div ref={panelRef}>
        <QuestionCard
          promptEn={hasImageOptions ? 'Find the matching image' : item.prompt_text_en}
          promptFr={item.prompt_text_fr || (item.prompt_type === 'audio' ? item.audio_text_fr : null)}
          promptImage={item.prompt_image}
          answer={(item.options || []).find(o => o.correct)?.text}
          accentColor={color}
        >
          {item.prompt_type === 'audio' && item.audio_text_fr && !item.prompt_text_fr && (
            <button
              data-testid={`audio-clue-team-${team}`}
              onClick={() => playAudio('la_course', item.item_id, item.audio_text_fr)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                marginTop: '0.25rem', color: isPlaying ? 'var(--primary)' : 'var(--text-muted)',
                fontSize: '0.85rem', fontWeight: 600, fontStyle: 'italic',
                background: isPlaying ? 'rgba(76,110,245,0.08)' : 'transparent',
                border: 'none', padding: '0.3rem 0.75rem', borderRadius: '0.5rem',
                cursor: 'pointer', fontFamily: 'Fredoka',
              }}>
              <Volume2 size={16} /> {isPlaying ? 'Playing...' : 'Play Audio Clue'}
            </button>
          )}
        </QuestionCard>
      </div>

      {hasImageOptions ? (
        <div data-testid={`question-panel-team-${team}`} style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem',
        }}>
          {(item.options || []).map((opt, idx) => (
            <button key={`${item.item_id}-${idx}`} data-testid={`option-team${team}-${idx}`}
              className="option-btn"
              onClick={(e) => onAnswer(team, idx, e.currentTarget)}
              disabled={fb === 'correct'}
              style={{
                borderLeftColor: color, borderLeftWidth: 4,
                padding: '0.5rem', display: 'flex',
                alignItems: 'center', justifyContent: 'center', minHeight: 0,
              }}
            >
              {opt.image && (
                <img
                  src={imgBase ? `${imgBase}/${opt.image}` : `/assets/content/${opt.image}`}
                  alt=""
                  style={{
                    width: '100%', height: 'auto', maxHeight: 130,
                    objectFit: 'contain', borderRadius: '0.5rem',
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </button>
          ))}
        </div>
      ) : (
        <div data-testid={`question-panel-team-${team}`} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {(item.options || []).map((opt, idx) => (
            <button key={`${item.item_id}-${idx}`} data-testid={`option-team${team}-${idx}`}
              className="option-btn"
              onClick={(e) => onAnswer(team, idx, e.currentTarget)}
              disabled={fb === 'correct'}
              style={{
                borderLeftColor: color, borderLeftWidth: 5,
                fontSize: '1.1rem', minHeight: 48, padding: '0.6rem 1rem',
              }}
            >
              {opt.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
